// components/core/Button.tsx
import React from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Button variant types for different visual styles
 */
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'link';

/**
 * Button size options
 */
type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Icon position options
 */
type IconPosition = 'start' | 'end' | 'top';

/**
 * Props for the Button component
 */
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The visual style variant of the button */
  variant?: ButtonVariant;
  /** The size of the button */
  size?: ButtonSize;
  /** Icon to display before the button text */
  startIcon?: React.ReactNode;
  /** Icon to display after the button text */
  endIcon?: React.ReactNode;
  /** Position of the icon relative to text (start, end, or top) */
  iconPosition?: IconPosition;
  /** If true, the button will take up the full width of its container */
  fullWidth?: boolean;
  /** Loading state of the button */
  isLoading?: boolean;
  /** If true, centers the button content */
  centered?: boolean;
}

/**
 * Size styles for the button
 */
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'text-sm px-3 py-1.5',
  md: 'text-base px-4 py-2',
  lg: 'text-lg px-6 py-3'
};

/**
 * A versatile button component with multiple variants and sizes.
 * Supports icons in different positions including above text.
 * Follows accessibility best practices and supports loading states and full width.
 */
/**
 * Forwards its ref so a caller can move focus to the button. Returning focus
 * to the control that opened an editor is the difference between a keyboard
 * user keeping their place and being dropped at the top of the document, and
 * `<button>` already receives every other prop this way.
 */
/** Options for {@link buttonClasses}. */
export interface ButtonClassOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isVertical?: boolean;
  centered?: boolean;
  isLoading?: boolean;
  className?: string;
}

/**
 * The class recipe a `Button` wears, available on its own.
 *
 * Exported because some things that must *look* like buttons cannot *be*
 * buttons: a control that navigates is a link, and a link is what lets it be
 * opened in a new tab or middle-clicked. Putting an `<a>` around a `<button>`
 * would nest two interactive elements, which is invalid and announces twice.
 *
 * Kept here rather than copied at the call site so the two can never drift --
 * a link styled from a stale copy of this recipe is a button that looks
 * almost right.
 */
export function buttonClasses({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isVertical = false,
  centered = true,
  isLoading = false,
  className,
}: ButtonClassOptions = {}): string {
  return twMerge(
    clsx(
      // Base styles
      `button`,
      `button-${variant}`,
      'relative rounded-lg font-medium transition-colors duration-200',
      // No focus utility: the global `:focus-visible` outline (globals.css)
      // is the ring. `focus:outline-none` here outranked it and painted it
      // transparent, so keyboard focus was invisible (A11Y-006).
      'disabled:opacity-50 disabled:cursor-not-allowed',

      // Size specific styles
      variant !== 'link' && sizeStyles[size],

      // Width styles
      fullWidth ? 'w-full' : 'w-auto',

      // Content alignment
      'inline-flex items-center',
      isVertical ? 'flex-col' : 'flex-row',
      centered && 'justify-center',

      // Loading state styles
      isLoading && 'cursor-wait',

      // Custom classes
      className
    )
  );
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  startIcon,
  endIcon,
  iconPosition = 'start',
  fullWidth = false,
  isLoading = false,
  centered = true,
  className,
  children,
  disabled,
  ...props
}, ref) => {
  
  // Determine if we're using the vertical layout
  const isVertical = iconPosition === 'top';
  
  // Get the icon based on position
  const icon = iconPosition === 'end' ? endIcon : startIcon;
  
  // Combine styles using clsx and tailwind-merge
  const buttonStyles = buttonClasses({
    variant,
    size,
    fullWidth,
    isVertical,
    centered,
    isLoading,
    className,
  });

  return (
    <button
      ref={ref}
      className={buttonStyles}
      disabled={disabled || isLoading}
      data-variant={variant}
      {...props}
    >
      {isLoading && (
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {/* Loading spinner */}
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        </span>
      )}
      
      <span className={clsx('inline-flex items-center', isVertical ? 'flex-col' : 'flex-row', isLoading && 'invisible')}>
        {isVertical ? (
          <>
            {icon && <div className="mb-1">{icon}</div>}
            <div className={clsx(isVertical && "text-xs font-medium")}>{children}</div>
          </>
        ) : (
          <>
            {startIcon && <span className="mr-2">{startIcon}</span>}
            {children}
            {endIcon && <span className="ml-2">{endIcon}</span>}
          </>
        )}
      </span>
    </button>
  );
});

Button.displayName = 'Button';

export default Button;